<template>
  <div class="vch-forgot-password">
    <div class="vch-forgot-password-title">
      <h1 class="vch-forgot-password__title heading-xl uppercase">
        {{ currentTitle }}
        <span class="heading-xl sub-heading">{{ titleSecondary }}</span>
      </h1>
      <span class="vch-forgot-password__instructions">{{
        formInstruction
      }}</span>
    </div>
    <template v-if="!formSuccess">
      <div class="vch-grid">
        <div class="vch-col-12 vch-col-offset-md-4 vch-col-md-5">
          <slot name="form">Form Here</slot>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="vch-grid">
        <div class="vch-col-12 vch-col-offset-md-4 vch-col-md-5">
          <slot name="form">Form Here</slot>
        </div>
      </div>
      <div class="vch-grid">
        <div class="vch-col-12 vch-col-offset-md-4 vch-col-md-5">
          <div class="vch-forgot-password__email-notification">
            <h1 class="email-sent heading-xl">EMAIL SENT</h1>
            <span class="email-instructions">{{ emailConfirmationMsg }}</span>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
<script>
import { parseSitecoreForm, bindFormField } from 'Utils/form';
import { mixinFormRecaptcha } from 'Mixins/formRecaptcha/formRecaptcha';
import { pushEvent } from 'Utils/datalayer';
export default {
  name: 'vch-forgot-password',
  mixins: [mixinFormRecaptcha],
  props: {
    formTitle: {
      type: String,
      required: true,
    },
    formInstruction: {
      type: String,
    },
    titleSubheading: {
      type: String,
      required: true,
    },
    testHost: {
      type: String,
      required: false,
    },
    confirmButtonText: {
      type: String,
      required: true,
    },
    errorMessageFormIncomplete: {
      type: String,
      required: true,
    },
    errorMessageFormTerms: {
      type: String,
      required: true,
    },
    emailConfirmationMsg: {
      type: String,
      required: true,
    },
    defaultErrorMessage: {
      type: String,
      required: false,
      default: 'An error occured while processing your request.',
    },
    formDatalayer: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      currentTitle: this.formTitle,
      titleSecondary: this.titleSubheading,
      formSuccess: false,
      useEmailFlow: false,
      fields: [],
      formProperties: null,
      parser: new DOMParser(),
      errorWrapperNode: null,
      datalayerForm: null,
    };
  },
  computed: {
    formActionUrl() {
      return process.env.NODE_ENV === 'production'
        ? this.formProperties.formAction
        : `${this.testHost}${this.formProperties.formAction}`;
    },
  },
  mounted() {
    const formNode = this.$slots.form[0].children[0].children[0];
    if (!formNode?.elm) {
      return;
    }

    // Collect the base form properties and hijack form submit
    this.formProperties = parseSitecoreForm(formNode?.elm);
    formNode.elm.addEventListener('submit', this.onSubmit);

    // Create the error message wrapper
    this.errorWrapperNode = document.createElement('div');
    this.errorWrapperNode.setAttribute('role', 'status');
    this.errorWrapperNode.classList.add('vch-contact-form__errorSummary');
    const submitButton = formNode?.elm.querySelector('input[type="submit"]');
    submitButton.parentNode.insertBefore(this.errorWrapperNode, submitButton);

    // Traverse the form and collect all the inputs into a single list
    formNode.children
      .filter((node) => node.tag === 'div' || node.tag === 'input')
      .forEach((node) => {
        const foundFieldInfo = bindFormField(node);
        if (foundFieldInfo.nestedFieldInfo.length) {
          this.fields.push(...foundFieldInfo.nestedFieldInfo);
        }
        this.fields.push(foundFieldInfo);
      });
    this.datalayerForm = JSON.parse(this.formDatalayer);
  },

  methods: {
    setErrorMessage(errorMessage) {
      this.errorWrapperNode.innerText = errorMessage;
    },
    validateFields() {
      const validationErrors = [];
      let hasTermsError = false;
      this.fields.forEach((field) => {
        const fieldErrors = field.validate();
        if (fieldErrors.length) {
          hasTermsError = true;
        }
        validationErrors.push(...fieldErrors);
      });

      if (hasTermsError) {
        this.setErrorMessage(this.errorMessageFormTerms);
        return false;
      } else if (validationErrors.length > 0) {
        this.setErrorMessage(this.errorMessageFormIncomplete);
        return false;
      }
      return true;
    },
    async onSubmit(e) {
      e.preventDefault();
      if (!this.validateFields()) return;

      let submitButton = document.querySelector('.vch-form-submit');
      // Recaptcha Flow via Mixin
      this.peformRecaptcha()
        .then(async (canSubmit) => {
          if (canSubmit) {
            if (submitButton) {
              submitButton.setAttribute('disabled', 'disabled');
            }
            await fetch(this.formActionUrl, {
              method: 'POST',
              mode: process.env.NODE_ENV === 'production' ? 'cors' : 'no-cors',
              body: this.assembleFormData(),
            })
              .then((response) => {
                if (response.status >= 400 && response.status < 600) {
                  throw new Error(this.defaultErrorMessage);
                }
                return response.text();
              })
              .then((response) => {
                const htmlResponse = this.parser.parseFromString(
                  response,
                  'text/xml'
                );
                const errorWrapper = htmlResponse.querySelector(
                  '.validation-summary-errors'
                );
                if (errorWrapper) {
                  const serverErrorMsgs = errorWrapper.querySelectorAll('li');
                  const summaryErrors = Array.from(serverErrorMsgs).map(
                    (msg) => msg.textContent
                  );
                  this.setErrorMessage(summaryErrors[0]);
                  if (submitButton) {
                    submitButton.removeAttribute('disabled');
                  }
                } else {
                  this.formSuccess = true;
                  this.pushSubmitTags();
                  setTimeout(() => {
                    window.scrollTo({
                      top: 0,
                      behavior: 'smooth',
                    });
                  }, 100);
                }
              })
              .catch((error) => {
                this.setErrorMessage(error.message);
                if (submitButton) {
                  submitButton.removeAttribute('disabled');
                }
              });
          }
        })
        .catch(() => {
          this.setErrorMessage(this.defaultErrorMessage);
          if (submitButton) {
            submitButton.setAttribute('disabled', 'disabled');
          }
        });
    },
    assembleFormData() {
      const formData = new FormData();
      this.fields.forEach((field) => {
        if (field.inputElement) {
          formData.append(field.inputElement.name, field.inputElement.value);
        }
        field.hiddenFields.forEach((hiddenField) => {
          if (hiddenField.name) {
            formData.append(hiddenField.name, hiddenField.value);
          }
        });
      });
      return formData;
    },
    pushSubmitTags() {
      pushEvent({
        event: this.datalayerForm?.data?.eventProperties?.event,
        event_name: this.datalayerForm?.data?.eventProperties?.event_name,
        ecommerce: this.datalayerForm?.data?.eventProperties?.ecommerce,
        eventCategory: this.datalayerForm?.data?.eventProperties?.eventCategory,
        eventAction: this.datalayerForm?.data?.eventProperties?.eventAction,
        eventLabel: this.datalayerForm?.data?.eventProperties?.eventLabel,
      });
    },
    pushReturnTag() {
      pushEvent({
        event: 'uaevent',
        ecommerce: 'undefined',
        eventCategory: 'appendix',
        eventAction: 'select::vch-forgot-password',
        eventLabel: `${this.confirmButtonText.toLowerCase()}::${
          window.location.origin
        }`,
        event_name: 'body_button_click',
        cta_name: `${this.confirmButtonText.toLowerCase()}`,
        link_url: `${window.location.origin}`,
        module_name: 'vch-forgot-password',
      });
    },
  },
};
</script>

<style lang="scss" src="./forgot-password.scss"></style>
